import "./LandingScreen.css";
import "../components/marketing/marketing-shared.css";
import { useEffect } from "react";
import Seo from "../components/Seo";
import MarketingFonts from "../components/MarketingFonts";
import Hero from "../components/marketing/Hero";
import CapabilityGrid from "../components/marketing/CapabilityGrid";
import HowItWorks from "../components/marketing/HowItWorks";
import FeatureRow from "../components/marketing/FeatureRow";
import ScanToOrderScene from "../components/marketing/ScanToOrderScene";
import MidPageCta from "../components/marketing/MidPageCta";
import LiveDemoCallout from "../components/marketing/LiveDemoCallout";
import PricingSection from "../components/marketing/PricingSection";
import FaqSection from "../components/marketing/FaqSection";
import TrustSection from "../components/marketing/TrustSection";
import FinalCta from "../components/marketing/FinalCta";
import StickyMobileCta from "../components/marketing/StickyMobileCta";

import ordersImg from "../assets/marketing/orders.webp";
import tablesImg from "../assets/marketing/tables.webp";
import statsImg from "../assets/marketing/stats.webp";
import qrCustomizerImg from "../assets/marketing/qr-customizer.webp";

const softwareAppJsonLd = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Tablify",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  description:
    "Digitális étlap, QR-kódos rendelés és teljes éttermi irányítás egy rendszerben: asztalkezelés, valós idejű rendeléskövetés és statisztikák.",
  offers: {
    "@type": "Offer",
    price: "1990",
    priceCurrency: "HUF",
  },
};

const LandingScreen = () => {
  // Same body class AboutScreen/ContactScreen/PrivacyScreen/TermsScreen
  // already add — index.css only strips Bootstrap's <Container> max-width
  // and <main>'s py-3 padding (which otherwise box in and vertically gap
  // every section from the header) when body.landing-page is present.
  // This was the one screen missing it, so it was the one screen still
  // boxed in.
  useEffect(() => {
    document.body.classList.add("landing-page");

    return () => {
      document.body.classList.remove("landing-page");
    };
  }, []);

  useEffect(() => {
    const fadeElements = document.querySelectorAll(".fade-up");

    // Content must never depend on the observer actually firing to become
    // visible — respect prefers-reduced-motion, and fall back to revealing
    // everything (e.g. a direct /#pricing anchor jump, or the observer
    // simply not having fired yet) rather than leaving sections blank.
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (prefersReducedMotion) {
      fadeElements.forEach((el) => el.classList.add("active"));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active");
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: "0px 0px -10% 0px",
      },
    );

    fadeElements.forEach((el) => observer.observe(el));

    const safetyNet = setTimeout(() => {
      fadeElements.forEach((el) => el.classList.add("active"));
    }, 2000);

    return () => {
      observer.disconnect();
      clearTimeout(safetyNet);
    };
  }, []);

  return (
    <div className="landing-page">
      <Seo
        title="QR étlap és rendelés vendéglátóhelyeknek"
        description="Digitális étlap, QR-kódos rendelés és teljes éttermi irányítás egy rendszerben. Asztalkezelés, valós idejű rendeléskövetés és statisztikák éttermeknek."
        path="/"
        jsonLd={softwareAppJsonLd}
      />

      <MarketingFonts />

      <Hero />
      <CapabilityGrid />
      <HowItWorks />

      <section className="landing-features-section" id="features">
        <FeatureRow
          eyebrow="FŐ FUNKCIÓK"
          title="Modern digitális étlap és gyors rendelés"
          description="Nyújts modern éttermi élményt vendégeidnek QR-kódos rendelési rendszerrel és mobilbarát digitális étlappal."
          visual={<ScanToOrderScene />}
          items={[
            {
              title: "QR-kódos rendelés",
              desc: "A vendégek azonnal rendelhetnek telefonjukról, alkalmazás letöltése nélkül.",
            },
            {
              title: "Képes digitális étlap",
              desc: "Mutasd be ételeidet modern, látványos és mobiloptimalizált formában.",
            },
            {
              title: "Gyors menükezelés",
              desc: "Frissítsd az ételeket, árakat és kategóriákat néhány kattintással, valós időben.",
            },
          ]}
        />

        <FeatureRow
          reverse
          eyebrow="RENDELÉSKEZELÉS"
          title="Kezeld a rendeléseket valós időben — asztaltól a konyháig"
          description="Minden rendelés — akár a vendégtől, akár a pincértől érkezik — azonnal megjelenik a kezelőfelületeden, státusszal és hanggal jelezve."
          image={{
            src: ordersImg,
            alt: "Tablify admin felület, folyamatban lévő rendeléssel és státusszal",
          }}
          items={[
            {
              title: "Élő rendeléskövetés",
              desc: "Az új rendelések azonnal megjelennek, hang- és böngészőértesítéssel.",
            },
            {
              title: "Pincér rendelésfelvétel",
              desc: "Munkatársaid gyors, POS-szerű felülettel vehetnek fel rendeléseket asztalhoz.",
            },
            {
              title: "Rendelésstátusz-kezelés",
              desc: "Készül, kész, teljesítve — kövesd végig minden rendelés útját a konyhától az asztalig.",
            },
          ]}
        />

        <FeatureRow
          eyebrow="ASZTALKEZELÉS"
          title="Lásd át az éttermed egyetlen pillantással"
          description="Alakítsd ki éttermed digitális alaprajzát, és kövesd valós időben, mely asztal foglalt, mely szabad."
          image={{
            src: tablesImg,
            alt: "Tablify asztalkezelő felület, húzható asztalpozíciókkal",
          }}
          items={[
            {
              title: "Egyedi teremrajz",
              desc: "Húzd az asztalokat a valós elrendezés szerinti helyükre — a pozíció automatikusan mentésre kerül.",
            },
            {
              title: "Valós idejű asztalstátusz",
              desc: "Lásd egy pillantással, mely asztal foglalt, és mikor szabadul fel.",
            },
            {
              title: "Vendéglétszám és rendelések",
              desc: "Minden asztalhoz és vendéglétszámhoz kapcsolódó rendelés egy helyen.",
            },
          ]}
        />
      </section>

      <MidPageCta />

      <section className="landing-features-section">
        <FeatureRow
          reverse
          eyebrow="DASHBOARD & STATISZTIKÁK"
          title="Ismerd a számaidat — bevétel, csúcsidők, legnépszerűbb ételek"
          description="Minden fontos szám egy helyen: napi és havi bevétel, átlagos kosárérték, legnépszerűbb termékek és a legforgalmasabb időszakok."
          image={{
            src: statsImg,
            alt: "Tablify statisztikai dashboard bevételi és rendelési grafikonokkal",
          }}
          items={[
            {
              title: "Bevétel és rendelésszám",
              desc: "Napi, heti és havi bontásban, grafikonon is.",
            },
            {
              title: "Legnépszerűbb ételek",
              desc: "Lásd, mely termékek és kategóriák fogynak a legjobban.",
            },
            {
              title: "Csúcsidőszakok",
              desc: "Tudd előre, mikor lesz a legnagyobb forgalom — és tervezz ez alapján.",
            },
          ]}
        />

        <FeatureRow
          eyebrow="EGYEDI QR KÓDOK"
          title="QR kódok, amik a márkádat is képviselik"
          description="Ne egy sablon QR kódot tégy ki az asztalokra — testreszabhatod a színeket, a stílust, sőt a saját logódat is beleépítheted."
          image={{
            src: qrCustomizerImg,
            alt: "Tablify QR kód testreszabó felület élő előnézettel",
          }}
          items={[
            {
              title: "Teljes vizuális testreszabás",
              desc: "Szín, minta, sarokstílus és logó — mind a te márkádra hangolva.",
            },
            {
              title: "Beolvashatóság-ellenőrzés",
              desc: "Élő visszajelzés, hogy a QR kód a testreszabás után is jól olvasható marad-e.",
            },
            {
              title: "PNG, SVG vagy PDF export",
              desc: "Töltsd le nyomtatásra kész formátumban, bármilyen felbontásban.",
            },
          ]}
        />
      </section>

      <LiveDemoCallout />
      <PricingSection />
      <FaqSection />
      <TrustSection />
      <FinalCta />
      <StickyMobileCta />
    </div>
  );
};

export default LandingScreen;
